'use client'

import { updateSessionStorage } from '@/lib/sessionStorage'
import { cn } from '@/lib/utils'
import { cva } from 'class-variance-authority'
import { useRouter } from 'next/navigation'
import { ReactNode, memo, useCallback, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { getMainLandingTrackingProps } from '@/lib/main-landing'
import posthog from 'posthog-js'

export type Option = {
  icon?: ReactNode
  label: string
  value: string
}

type QuizItemVariants = 'primary' | 'primary-gradient' | 'primary-gradient-bordered'

type QuizItemRoundingVariants = 'full' | 'lg' | 'none'

type QuizOptionProps = {
  option: Option
  onSelect: () => void
  selected: boolean
  className?: string
  variant?: QuizItemVariants
  rounded?: QuizItemRoundingVariants
}

const quizItemVariants = cva(`cursor-pointer items-center before:absolute before:inset-[2px]`, {
  variants: {
    variant: {
      primary: 'border border-border bg-card',
      'primary-gradient': `bg-muted group-hover:before:bg-primary-hover`,
      'primary-gradient-bordered': `bg-muted group-hover:before:bg-primary-hover`,
    },
    rounded: {
      full: 'rounded-full group-hover:before:rounded-full',
      lg: 'rounded-lg group-hover:before:rounded-lg',
      none: 'rounded-none group-hover:before:rounded-none',
    },
  },
  defaultVariants: {
    variant: 'primary',
    rounded: 'none',
  },
})

const quizItemSelectedVariants = cva('', {
  variants: {
    variant: {
      primary: 'before:bg-muted',
      'primary-gradient': `before:bg-primary-hover`,
      'primary-gradient-bordered': `before:bg-primary-hover`,
    },
    rounded: {
      full: 'rounded-full before:rounded-full',
      lg: 'rounded-lg before:rounded-lg',
      none: 'rounded-none before:rounded-none',
    },
  },
  defaultVariants: {
    variant: 'primary',
    rounded: 'none',
  },
})

const quizItemContainerVariants = cva('relative group p-[2px]', {
  variants: {
    variant: {
      primary: 'hover:bg-primary',
      'primary-gradient': 'hover:bg-primary-gradient',
      'primary-gradient-bordered': `bg-border hover:bg-primary-gradient`,
    },
    rounded: {
      full: 'rounded-full',
      lg: 'rounded-lg',
      none: 'rounded-none',
    },
  },
  defaultVariants: {
    variant: 'primary',
    rounded: 'none',
  },
})

const quizItemContainerSelectedVariants = cva('', {
  variants: {
    variant: {
      primary: 'bg-primary',
      'primary-gradient': 'bg-primary-gradient',
      'primary-gradient-bordered': `bg-primary-gradient`,
    },
    rounded: {
      full: 'rounded-full',
      lg: 'rounded-lg',
      none: 'rounded-none',
    },
  },
  defaultVariants: {
    variant: 'primary',
    rounded: 'none',
  },
})

export type QuizVariants = 'row' | 'card'

const OptionRow = memo(
  ({ option, className, selected, variant, rounded, onSelect }: QuizOptionProps) => {
    return (
      <div
        className={cn(
          quizItemContainerVariants({ variant, rounded }),
          // `transition-all` made the browser watch every animatable property on
          // the most-tapped element in a 16-step funnel, paired with a hover
          // shadow that fires on every card the pointer sweeps across. Narrowed
          // to what actually changes, plus press feedback so a tap is
          // acknowledged before the next step renders.
          'transition-[box-shadow,border-color,background-color,transform] duration-200 ease-out',
          'active:scale-[0.99] hover:shadow-[0_0_16px_rgb(104_255_122_/_0.16)]',
          {
            [quizItemContainerSelectedVariants({ variant, rounded })]: selected,
          },
          className,
        )}
      >
        <div
          className={cn(quizItemVariants({ variant, rounded }), 'min-h-14 w-full', {
            [quizItemSelectedVariants({ variant, rounded })]: selected,
          })}
          onClick={onSelect}
        >
          <div className="absolute inset-0 flex min-h-12 items-center gap-4 px-6 py-3">
            <span className="text-xl">{option?.icon}</span>
            <span className="font-body text-base leading-snug text-foreground">{option.label}</span>
          </div>
        </div>
      </div>
    )
  },
)

const OptionCard = memo(
  ({ option, className, selected, onSelect, variant, rounded }: QuizOptionProps) => {
    return (
      <div
        className={cn(
          quizItemContainerVariants({ variant, rounded }),
          'transition-transform duration-200 ease-out active:scale-[0.99]',
          {
            activeVariant: selected,
          },
          className,
        )}
      >
        <div
          className={cn(quizItemVariants({ variant, rounded }), 'h-32 w-full')}
          onClick={onSelect}
        >
          <div className="absolute inset-0 flex flex-col justify-center text-center">
            <p className="mx-auto text-4xl">{option.icon}</p>
            <p className="font-body mt-2 text-base">{option.label}</p>
          </div>
        </div>
      </div>
    )
  },
)

const QuizVariantsMap = {
  card: OptionCard,
  row: OptionRow,
}

type FunnelQuizProps = {
  options: Option[]
  variant: QuizVariants
  itemVariant?: QuizItemVariants
  itemRounded?: QuizItemRoundingVariants
  className?: string
  multiSelect?: boolean
  value: string[] | string
  onChange: ((value: string) => void) | ((value: string[]) => void)
}

function Quiz({
  options,
  variant,
  itemVariant,
  itemRounded,
  className,
  multiSelect,
  value,
  onChange,
}: FunnelQuizProps) {
  const onSelectionChange = useCallback(
    (newValue: string) => {
      if (Array.isArray(value) && multiSelect) {
        const newValues = value.includes(newValue)
          ? value.filter((x) => x !== newValue)
          : [...value, newValue]
        onChange(newValues as any)
      } else {
        onChange(newValue as any)
      }
    },
    [value, multiSelect, onChange],
  )

  const QuizItem = useMemo(() => QuizVariantsMap[variant], [variant])

  const checkSelection = useCallback(
    (checkValue: string): boolean => {
      if (Array.isArray(value) && multiSelect) {
        return value.includes(checkValue)
      }
      return value === checkValue
    },
    [value, multiSelect],
  )

  return (
    <div
      className={cn(
        'flex',
        {
          'grid grid-cols-2 justify-center gap-4': variant === 'card',
          'flex w-full flex-col gap-3': variant === 'row',
        },
        className,
      )}
    >
      {options.map((option: Option, index) => (
        <QuizItem
          key={index}
          option={option}
          onSelect={() => onSelectionChange(option.value)}
          selected={checkSelection(option.value)}
          variant={itemVariant}
          rounded={itemRounded}
        />
      ))}
    </div>
  )
}

type PersistedSingleQuizProps = {
  options: Option[]
  redirectURL?: string
  variant: QuizVariants
  itemVariant?: QuizItemVariants
  itemRounded?: QuizItemRoundingVariants
  storeKey: string
  className?: string
  onNext?: (v: string) => void
  posthogProperties?: Record<string, string | number>
}

const PersistedSingleQuiz = ({
  options,
  redirectURL,
  variant,
  itemVariant,
  itemRounded,
  storeKey,
  className,
  onNext,
  posthogProperties,
}: PersistedSingleQuizProps) => {
  const [value, setValue] = useState('')
  const router = useRouter()

  const handleNext = useCallback(
    (v: string) => {
      if (redirectURL) {
        return router.push(redirectURL)
      }

      if (onNext) {
        return onNext(v)
      }

      return () => {}
    },
    [onNext, redirectURL, router],
  )

  const onChange = useCallback(
    (v: string) => {
      setValue(v)
      updateSessionStorage('user', { [storeKey]: v })
      posthog.capture('quiz_answer_selected', {
        question: storeKey,
        answer: v,
        ...posthogProperties,
        ...getMainLandingTrackingProps(),
      })
      handleNext(v)
    },
    [handleNext, posthogProperties, storeKey],
  )

  return (
    <Quiz
      onChange={onChange}
      options={options}
      value={value}
      variant={variant}
      className={className}
      itemVariant={itemVariant}
      itemRounded={itemRounded}
    />
  )
}

type PersistedMultiQuizProps = {
  options: Option[]
  redirectURL?: string
  variant: QuizVariants
  itemVariant?: QuizItemVariants
  itemRounded?: QuizItemRoundingVariants
  storeKey: string
  className?: string
  onNext?: (v: string[]) => void
  buttonText?: string
  footerText?: string
  posthogProperties?: Record<string, string | number>
}

const PersistedMultiQuiz = ({
  options,
  redirectURL,
  variant,
  itemVariant,
  itemRounded,
  storeKey,
  className,
  onNext,
  buttonText = 'Continue',
  footerText,
  posthogProperties,
}: PersistedMultiQuizProps) => {
  const [value, setValue] = useState<string[]>([])
  const router = useRouter()

  const handleNext = useCallback(() => {
    updateSessionStorage('user', { [storeKey]: value })
    posthog.capture('quiz_answer_selected', {
      question: storeKey,
      answer: value,
      answer_count: value.length,
      ...posthogProperties,
      ...getMainLandingTrackingProps(),
    })

    if (redirectURL) {
      return router.push(redirectURL)
    }

    if (onNext) {
      return onNext(value)
    }

    return () => {}
  }, [value, redirectURL, onNext, posthogProperties, router, storeKey])

  const onChange = useCallback((v: string[]) => {
    setValue(v)
  }, [])

  return (
    <div className="relative h-full">
      <div className="pb-24">
        <Quiz
          onChange={onChange}
          options={options}
          value={value}
          variant={variant}
          className={className}
          itemVariant={itemVariant}
          itemRounded={itemRounded}
          multiSelect={true}
        />
      </div>

      <div className="fixed inset-x-0 bottom-0 flex flex-col items-center bg-linear-to-t from-background via-background px-6 pb-6 pt-8">
        {footerText && (
          <p className="text-muted-foreground mb-4 text-center text-sm font-medium">{footerText}</p>
        )}
        <Button
          variant="primary"
          className="w-full max-w-sm"
          size="xl"
          onClick={handleNext}
          disabled={value.length === 0}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  )
}

export { OptionCard, OptionRow, PersistedSingleQuiz, PersistedMultiQuiz }
export default Quiz
